import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useEffect, useRef, useState } from "react";
import { Bar, BarChart, Cell, Legend, Tooltip as ChartTooltip, XAxis, YAxis } from "recharts";

import { ChartFrame } from "@/components/data";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { MONTH_MOVE_LEGEND, monthMoveClass, monthMoves, SKU_DRAWER_WIDTH } from "@/lib/monthMove";

import { getSoldCostsDetail, type SoldCostsDetail, type SoldCostsDetailMonth, type SoldCostsHeld, type SoldCostsKey, type SoldCostsSelection } from "./api";
import { COPY, messageFor, money, unitsLabel } from "./format";
import { heldFor, MONTH_STATE_COPY, monthState, stateTitle } from "./monthState";
import { blockSubtotal, chartSeries, expectedChartRow, expectedWindow, leafBlocks, padSoldCostsMonths, signedChartRows, totalPerUnit, trailingWindows, usedMonths, weightedBlocks, weightLabel, type ExpectedWindow, type LeafBlock, type LeafRow, type SoldCostsMode } from "./soldCostsModel";

const BAR_TOKENS = ["var(--color-ks-purple-1)", "var(--color-ks-purple-2)", "var(--color-ks-purple-3)", "var(--color-ks-purple-4)", "var(--color-ks-purple-5)"];
const EXPECTED_HEADLINE = "bg-ks-purple-4 font-semibold text-ks-purple";
// C4 v1.6.5: the row labels and the Expected column stay put while the month columns scroll (same method as the tab's
// PINNED_COLUMN_CLASS). The edges are inset shadows, not borders (border-collapse can drop a border from a sticky cell).
const PINNED_LABEL = "sticky left-0 z-10 bg-background shadow-[inset_-1px_0_0_var(--color-ks-grey-4)]";
const PINNED_EXPECTED = "sticky right-0 z-10 shadow-[inset_1px_0_0_var(--color-ks-grey-4)]";

function valueCell(value: number | null | undefined, currency: string | null): string {
  return value === null || value === undefined ? "—" : money(value, currency);
}

function waitingGroups(month: SoldCostsDetailMonth): Set<string> {
  return new Set(month.covered.filter((entry) => entry.status === "product_cost_missing" || entry.waiting_for_sheet).map((entry) => entry.ofs_group));
}

function stateBadge(label: string, title?: string) {
  return <Badge variant="outline" className="ml-1" title={title}>{label}</Badge>;
}

const TRAILING_HEADER_TITLE = "Cost per unit over the last three closed months in view, weighted 50% the newest month, 30% the month before, 20% the oldest; rescaled to sum to 100% when fewer months are in view. A month with no cost posted for a row counts as zero. Computed from the monthly values Sold-COGS serves.";

function expectedHeader(window: ExpectedWindow, used: string[]): string {
  if (!window || used.length === 0) return "Expected (3-month trailing)";
  if (used.length === 3) return `Expected (3-month trailing) - ${weightLabel(used)}`;
  if (used.length === 2) return `Expected (3-month trailing) - 2 months used, ${weightLabel(used)}`;
  return "Expected (3-month trailing) - 1 month used";
}

function monthsUsedText(window: ExpectedWindow, used: string[]): string {
  if (!window) return "No closed month in view.";
  if (used.length === 0) return "No units sold in the closed months in view.";
  const dropped = window.window.filter((month) => !used.includes(month));
  return `Months used: ${used.join(", ")}.${dropped.length ? ` Left out, no units: ${dropped.join(", ")}.` : ""}`;
}

function noTrailingTitle(window: ExpectedWindow, used: string[]): string {
  if (!window) return "No closed month in view.";
  if (used.length === 0) return "No units sold in the closed months in view.";
  return used.length === 1 ? `No cost posted in ${used[0]}.` : `No cost posted in ${used[0]} to ${used[used.length - 1]}.`;
}

// The used months in which the leaf has no value (they count as zero). Looks the month up through `months` because
// perUnit is keyed by the served month string.
function zeroFilled(row: LeafRow, months: SoldCostsDetailMonth[], used: string[]): string[] {
  return used.filter((usedMonth) => {
    const key = months.find((month) => month.month.slice(0, 7) === usedMonth)?.month;
    return key === undefined || row.perUnit[key] === null || row.perUnit[key] === undefined;
  });
}

function trailingLeafCell(row: LeafRow, months: SoldCostsDetailMonth[], window: ExpectedWindow, used: string[], currency: string | null, includesWaiting: boolean) {
  const value = window ? row.perUnitTrailing3[window.month] : null;
  if (!window || value === null || value === undefined) {
    return <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} title={noTrailingTitle(window, used)}>—</TableCell>;
  }
  const zero = zeroFilled(row, months, used);
  const zeroTitle = zero.length ? `No cost posted in ${zero.join(", ")}; counted as zero in the weighted value.` : undefined;
  return (
    <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`}>
      {valueCell(value, currency)}
      {zero.length > 0 && stateBadge(`posted in ${used.length - zero.length} of ${used.length} months`, zeroTitle)}
      {includesWaiting && stateBadge("includes waiting month")}
    </TableCell>
  );
}

export function tooltipValue(value: unknown): string {
  return typeof value === "number" ? value.toFixed(2) : String(value);
}

export function assembleSoldCostsMonths(
  data: SoldCostsDetail | undefined,
  window: { from: string; to: string },
): { months: SoldCostsDetailMonth[]; held: SoldCostsHeld[] } {
  const servedMonths = data?.months ?? [];
  const held = data?.held?.filter((entry) => entry.entity_id === data.key.entity_id && entry.month.slice(0, 7) >= window.from && entry.month.slice(0, 7) <= window.to) ?? [];
  const months = [
    ...servedMonths,
    ...held.filter((entry) => !servedMonths.some((month) => month.month.slice(0, 7) === entry.month.slice(0, 7))).map((entry): SoldCostsDetailMonth => ({
      month: entry.month.slice(0, 7), units: "", amount: "", per_unit: null, per_unit_trailing_3: null,
      closed: false, version: 0, leaves: [], covered: [],
    })),
  ].sort((left, right) => left.month.localeCompare(right.month));
  return { months, held };
}

export function SoldCostsTable({
  blocks,
  months,
  keyData,
  mode,
  collapsed,
  onToggle,
  held,
  noData = new Set<string>(),
}: {
  blocks: LeafBlock[];
  months: SoldCostsDetailMonth[];
  keyData: SoldCostsKey;
  mode: SoldCostsMode;
  collapsed: ReadonlySet<string>;
  onToggle: (group: string) => void;
  held?: SoldCostsHeld[];
  noData?: ReadonlySet<string>;
}) {
  const weighted = weightedBlocks(blocks, months, held, keyData.entity_id);
  const modeKey = mode === "month" ? "perUnit" : "perUnitTrailing3";
  const window = expectedWindow(months, held, keyData.entity_id);
  const used = window ? usedMonths(months, window) : [];
  const columnWindows = trailingWindows(months, held, keyData.entity_id);
  const includesWaiting = window?.window.some((windowMonth) => months.some((month) => month.month.slice(0, 7) === windowMonth && month.waiting_for_sheet)) ?? false;
  const isHeld = (month: string): boolean => heldFor(held, keyData.entity_id, month) !== undefined;
  const windowWaiting = (index: number): boolean => columnWindows[index]?.window.some((windowMonth) => months.some((entry) => entry.month.slice(0, 7) === windowMonth && entry.waiting_for_sheet)) ?? false;
  const movesOf = (valueOf: (month: SoldCostsDetailMonth, index: number) => number | null | undefined) =>
    monthMoves(months.map((month, index) => (isHeld(month.month) ? null : valueOf(month, index))));
  const totalMoves = movesOf((month, index) =>
    (mode === "month" ? month.waiting_for_sheet : windowWaiting(index)) ? null : totalPerUnit(weighted, month.month, mode));
  const trailingTitle = !window ? "No closed month in view." : used.length === 0 ? "No units sold in the closed months in view." : undefined;
  const trailingTotal = window ? totalPerUnit(weighted, window.month, "trailing_3") : null;
  return (
    <>
      <Table>
        <TableCaption>{`Sold-COGS per unit by month, ${keyData.xero_sku}`}</TableCaption>
        <TableHeader>
          <TableRow>
            <TableHead className={PINNED_LABEL} />
            {months.map((month, index) => {
              const heldEntry = heldFor(held, keyData.entity_id, month.month);
              const state = monthState({ ...month, held: heldEntry !== undefined });
              const title = noData.has(month.month.slice(0, 7)) ? `No Sold-COGS for this SKU in ${month.month.slice(0, 7)}.` : heldEntry ? "" : stateTitle(month);
              const badge = state === "waiting" ? "waiting for sheet" : null;
              const columnWindow = columnWindows[index];
              const usedCount = mode === "trailing_3" && columnWindow ? usedMonths(months, columnWindow).length : 0;
              return <TableHead key={month.month} title={title || undefined}>{month.month}{noData.has(month.month.slice(0, 7)) && stateBadge("no data")}{badge && stateBadge(badge)}{(usedCount === 1 || usedCount === 2) && stateBadge(usedCount === 1 ? "1 month" : "2 months", "Fewer than three closed months with units sold in view up to this month, so the weights are rescaled.")}</TableHead>;
            })}
            <TableHead title={`${TRAILING_HEADER_TITLE} ${monthsUsedText(window, used)}`} className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`}>{expectedHeader(window, used)}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
        <TableRow>
          <TableHead scope="row" className={PINNED_LABEL}>Units</TableHead>
          {months.map((month) => {
            const heldEntry = heldFor(held, keyData.entity_id, month.month);
            return <TableCell key={month.month}>{noData.has(month.month.slice(0, 7)) || heldEntry ? "—" : unitsLabel(keyData, month)}</TableCell>;
          })}
          <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} />
        </TableRow>
        <TableRow className="font-bold">
          <TableHead scope="row" className={`${PINNED_LABEL} font-bold`}>Total Sold-COGS per unit</TableHead>
          {months.map((month, index) => {
            const heldEntry = heldFor(held, keyData.entity_id, month.month);
            if (heldEntry) return <TableCell key={month.month}>—</TableCell>;
            const trailingWaiting = mode === "trailing_3" && (columnWindows[index]?.window.some((windowMonth) => months.some((entry) => entry.month.slice(0, 7) === windowMonth && entry.waiting_for_sheet)) ?? false);
            return <TableCell key={month.month} className={monthMoveClass(totalMoves[index] ?? null)}>{valueCell(totalPerUnit(weighted, month.month, mode), keyData.currency)}{month.waiting_for_sheet && stateBadge("without Cost of Product", MONTH_STATE_COPY.waitingTitle)}{trailingWaiting && stateBadge("includes waiting month")}</TableCell>;
          })}
          <TableCell className={`${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} title={trailingTitle}>{valueCell(trailingTotal, keyData.currency)}{trailingTotal !== null && includesWaiting && stateBadge("includes waiting month")}</TableCell>
        </TableRow>
        {weighted.map((block) => {
          const blockMoves = block.covered ? [] : movesOf((month, index) =>
            (mode === "month" ? waitingGroups(month).has(block.group) : windowWaiting(index)) ? null : blockSubtotal(block, month.month, mode));
          const isCollapsed = collapsed.has(`${block.covered ? "covered:" : "spread:"}${block.group}`);
          const collapseKey = `${block.covered ? "covered:" : "spread:"}${block.group}`;
          return (
            <Fragment key={collapseKey}>
              <TableRow className="font-bold">
                <TableHead scope="row" className={`${PINNED_LABEL} font-bold`}>
                  <Button type="button" variant="ghost" size="xs" className="font-bold" data-nonmutating aria-expanded={!isCollapsed} onClick={() => onToggle(collapseKey)}>
                    {isCollapsed ? <ChevronRight aria-hidden /> : <ChevronDown aria-hidden />}{block.label}
                  </Button>
                </TableHead>
                {months.map((month, index) => {
                  const heldEntry = heldFor(held, keyData.entity_id, month.month);
                  if (heldEntry) return <TableCell key={month.month}>—</TableCell>;
                  const excludesProductCost = waitingGroups(month).has(block.group);
                  const trailingWaiting = mode === "trailing_3" && (columnWindows[index]?.window.some((windowMonth) => months.some((entry) => entry.month.slice(0, 7) === windowMonth && entry.waiting_for_sheet)) ?? false);
                  return <TableCell key={month.month} className={monthMoveClass(blockMoves[index] ?? null)}>{block.covered ? null : valueCell(blockSubtotal(block, month.month, mode), keyData.currency)}{excludesProductCost && stateBadge("without Cost of Product", MONTH_STATE_COPY.waitingTitle)}{!block.covered && trailingWaiting && stateBadge("includes waiting month")}</TableCell>;
                })}
                <TableCell className={block.covered ? `bg-background ${PINNED_EXPECTED}` : `${EXPECTED_HEADLINE} ${PINNED_EXPECTED}`} title={!block.covered ? trailingTitle : undefined}>
                  {block.covered || !window ? (block.covered ? null : "—") : valueCell(blockSubtotal(block, window.month, "trailing_3"), keyData.currency)}
                  {!block.covered && window && blockSubtotal(block, window.month, "trailing_3") !== null && includesWaiting && stateBadge("includes waiting month")}
                </TableCell>
              </TableRow>
              {!isCollapsed && block.rows.map((row) => {
                const rowMoves = block.covered ? [] : movesOf((month) => row[modeKey][month.month]);
                return (
                  <TableRow key={row.nodeId}>
                    <TableHead scope="row" className={`${PINNED_LABEL} pl-8 font-normal`}>{row.label}</TableHead>
                    {months.map((month, index) => {
                      if (heldFor(held, keyData.entity_id, month.month)) return <TableCell key={month.month}>—</TableCell>;
                      if (block.covered) return <TableCell key={month.month}>{row.coveredStatus[month.month] === "covered" ? "Covered by PCOGS" : row.coveredStatus[month.month] === "no_units" ? "No units sold" : row.coveredStatus[month.month] === "product_cost_missing" ? MONTH_STATE_COPY.waiting : "—"}</TableCell>;
                      const trailingWaiting = mode === "trailing_3" && (columnWindows[index]?.window.some((windowMonth) => months.some((entry) => entry.month.slice(0, 7) === windowMonth && entry.waiting_for_sheet)) ?? false);
                      return <TableCell key={month.month} className={monthMoveClass(rowMoves[index] ?? null)}>{valueCell(row[modeKey][month.month], keyData.currency)}{trailingWaiting && stateBadge("includes waiting month")}</TableCell>;
                    })}
                    {block.covered ? <TableCell className={`bg-background ${PINNED_EXPECTED}`} /> : trailingLeafCell(row, months, window, used, keyData.currency, includesWaiting)}
                  </TableRow>
                );
              })}
            </Fragment>
          );
        })}
        </TableBody>
      </Table>
      <p className="text-sm text-ks-grey-1">{MONTH_MOVE_LEGEND}</p>
      <p className="text-ks-ink text-sm">Expected = cost per unit over the last three closed months in view, weighted 50/30/20 from the newest month to the oldest (rescaled when fewer months are in view). Precision Pricing still prices on its own feed (fixed costs over the 12-month unit base) until Damien decides on trailing 3. {monthsUsedText(window, used)}</p>
    </>
  );
}

// C4 v1.6.3: one stacked bar per month; each spread block draws its costs above zero and its credits below, in its colour
// (credits lighter, legend shows the block once). The last bar is the Expected (trailing 3) value (DEC-1380 PB-54), lighter
// and dashed. The Total stays in the table; it is not a bar.
export function SoldCostsChart({ blocks, months, mode, sku, held, entityId }: { blocks: LeafBlock[]; months: SoldCostsDetailMonth[]; mode: SoldCostsMode; sku: string; held?: SoldCostsHeld[]; entityId: string }) {
  const weighted = weightedBlocks(blocks, months, held, entityId);
  const bars = chartSeries(blocks).filter((line) => !line.total).flatMap((line, index) => [
    { key: `${line.key}:costs`, name: line.name, fill: BAR_TOKENS[index % BAR_TOKENS.length], credit: false },
    { key: `${line.key}:credits`, name: `${line.name} (credits)`, fill: BAR_TOKENS[index % BAR_TOKENS.length], credit: true },
  ]);
  const expected = expectedChartRow(weighted, expectedWindow(months, held, entityId));
  const rows = [...signedChartRows(weighted, months, mode), ...(expected ? [expected] : [])];
  return (
    <ChartFrame title={`Sold-COGS per unit by month, ${sku}`} pointCount={rows.length * bars.length} emptyMessage={COPY.detailEmpty}>
      <BarChart data={rows} stackOffset="sign">
        <XAxis dataKey="month" /><YAxis /><ChartTooltip formatter={tooltipValue} /><Legend />
        {bars.map((bar) => (
          <Bar key={bar.key} dataKey={bar.key} name={bar.name} stackId="cost" fill={bar.fill} legendType={bar.credit ? "none" : "rect"} isAnimationActive={false}>
            {rows.map((row) => <Cell key={`${bar.key}:${row.month}`} fillOpacity={row.expected ? 0.35 : bar.credit ? 0.5 : 1} stroke={row.expected ? bar.fill : undefined} strokeDasharray={row.expected ? "4 3" : undefined} />)}
          </Bar>
        ))}
      </BarChart>
    </ChartFrame>
  );
}

export function SoldCostsSkuDialog({ selected, from, to, onClose }: { selected: SoldCostsSelection; from: string; to: string; onClose: () => void }) {
  const window = { from, to };
  const detail = useQuery({
    queryKey: ["pcogs", "sold-costs", "detail", window.from, window.to, selected],
    queryFn: () => getSoldCostsDetail(selected, window),
    retry: 1,
  });
  const [mode, setMode] = useState<SoldCostsMode>("month");
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(new Set());
  const toggle = (group: string) => setCollapsed((previous) => {
    const next = new Set(previous);
    if (next.has(group)) next.delete(group); else next.add(group);
    return next;
  });
  const data = detail.data?.data;
  const { months: served, held } = assembleSoldCostsMonths(data, window);
  const { months, noData } = padSoldCostsMonths(served, window.from, window.to);
  const blocks = leafBlocks(months);
  const tableRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const box = tableRef.current?.querySelector<HTMLElement>('[data-slot="table-container"]');
    if (box) box.scrollLeft = box.scrollWidth;
  }, [served.length]);
  const keyData = data?.key;
  const twins = (data?.entities?.length ?? 0) > 1;
  const title = keyData
    ? `${keyData.xero_sku} · ${keyData.product} · ${keyData.channel} ${keyData.location}${twins ? ` · ${keyData.entity_id}` : ""}`
    : selected.xero_sku;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className={`max-h-[90vh] overflow-y-auto ${SKU_DRAWER_WIDTH}`} aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
        {detail.isPending ? (
          <div className="grid gap-2" aria-label="Loading"><Skeleton className="h-8 w-full" /><Skeleton className="h-32 w-full" /></div>
        ) : detail.error ? (
          <p role="alert" className="text-ks-danger">{messageFor(detail.error)}</p>
        ) : !keyData || served.length === 0 ? (
          <p className="text-ks-ink">{COPY.detailEmpty}</p>
        ) : (
          <>
            <div role="radiogroup" aria-label="Sold Costs period" className="flex flex-wrap gap-2">
              {(["month", "trailing_3"] as const).map((item) => <Button key={item} variant={mode === item ? "default" : "outline"} role="radio" aria-checked={mode === item} data-nonmutating onClick={() => setMode(item)}>{item === "month" ? "Month" : "Trailing 3"}</Button>)}
            </div>
            <div ref={tableRef}><SoldCostsTable blocks={blocks} months={months} keyData={keyData} mode={mode} collapsed={collapsed} onToggle={toggle} held={held} noData={noData} /></div>
            <SoldCostsChart blocks={blocks} months={months} mode={mode} sku={keyData.xero_sku} held={held} entityId={keyData.entity_id} />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
